import { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { Stack, Paper, Typography, Grid, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import FullCalendar from '@fullcalendar/react';
import type { EventSourceFuncArg, EventClickArg, EventChangeArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateClickArg } from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import moment from 'moment-timezone';

import { useCalendarEventStore } from '../../stores/root_store';
import CalendarEventEditor from './CalendarEventEditor';
import type { RenderableEvent } from './CalendarEventEditor';
import NoCalendar from './NoCalendar';
import type { CalendarEvent, CalendarListItem } from '../../types/calendar';

dayjs.extend(timezone);
dayjs.extend(utc);

const FAKE_PREFIX = 'FAKE://';

// Fix the timezone issue with FullCalendar recurring events
// https://github.com/fullcalendar/fullcalendar/issues/5273#issuecomment-1012036833
rrulePlugin.recurringTypes[0].expand = function (errd, fr, de) {
  return errd.rruleSet
    .between(fr.start, fr.end, true)
    .map(
      (date: Date) =>
        new Date(de.createMarker(date).getTime() + date.getTimezoneOffset() * 60 * 1000)
    );
};

interface CalendarFrameProps {
  associatedType: string;
  associatedUuid: string;
  showChrome?: boolean;
  backTo?: string | null;
}

function getRenderableEvent(event: CalendarEvent): RenderableEvent {
  const start = event.start?.dateTime || event.start?.date || '';

  // Format rrule for FullCalendar
  const dtstart = moment(start).format('YYYYMMDDTHHmmss');
  const rrule = event.recurrence?.length
    ? `DTSTART:${dtstart}\n${event.recurrence.join('\n')}`
    : undefined;

  const bg = event.calendar_list_entry?.backgroundColor;
  const fg = event.calendar_list_entry?.foregroundColor;

  return {
    id: event.id,
    calendar_id: event.calendar?.id || '',
    title: event.summary,
    start: start,
    end: event.end?.dateTime || event.end?.date || '',
    allDay: !!event.start?.date,
    rrule: rrule,
    backgroundColor: bg,
    textColor: fg,
    borderColor: event.editable ? undefined : '#bbbbbbaa',
    description: event.description || '',
    location: event.location || '',
    isUnsaved: false,
    editable: ['owner', 'writer'].includes(event.calendar_list_entry?.access_role || ''),
  };
}

const CalendarFrame = observer(
  ({ associatedType, associatedUuid, showChrome = true, backTo = null }: CalendarFrameProps) => {
    const navigate = useNavigate();
    const calendarEventStore = useCalendarEventStore();

    const calendarRef = useRef<FullCalendar>(null);

    const [startStr, setStartStr] = useState('');
    const [endStr, setEndStr] = useState('');
    const [selectedEvent, setSelectedEvent] = useState<RenderableEvent | null>(null);
    const [events, setEvents] = useState<RenderableEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [lastDates, setLastDates] = useState('');
    const [calendarPreferences, setCalendarPreferences] = useState('');
    const [timezoneStr, setTimezone] = useState<string | null>(null);
    const [noCalendar, setNoCalendar] = useState(false);
    const [calendarList, setCalendarList] = useState<CalendarListItem[]>([]);

    // Fetch timezone and calendar preferences
    useEffect(() => {

      calendarEventStore
        .checkHasCalendar(associatedType, associatedUuid)
        .then((hasCalendar) => {
          if (hasCalendar && calendarEventStore.hasCalendarInfo) {
            setTimezone(calendarEventStore.hasCalendarInfo.timezone || null);
            setCalendarPreferences(calendarEventStore.hasCalendarInfo.preferences || '');
          } else {
            setNoCalendar(true);
          }
        })
        .catch(() => {
          setNoCalendar(true);
        });
    }, [associatedType, associatedUuid, calendarEventStore]);

    // Fetch synced calendars
    useEffect(() => {
      if (!associatedType || !associatedUuid) return;
      if (!startStr || !endStr) return;

      calendarEventStore
        .fetchSyncedCalendars(associatedType, associatedUuid)
        .then((calendars) => {
          setCalendarList(calendars);
        });
    }, [timezoneStr, startStr, endStr, associatedType, associatedUuid, calendarEventStore]);

    // Fetch events
    useEffect(() => {
      if (!startStr || !endStr) return;

      const dateKey = startStr.split('T')[0] + endStr.split('T')[0];
      if (lastDates === dateKey) return;

      setLastDates(dateKey);
      setLoading(true);

      const s = moment(startStr).format('YYYY-MM-DDTHH:mm:ssZ');
      const e = moment(endStr).format('YYYY-MM-DDTHH:mm:ssZ');

      calendarEventStore
        .fetchEvents(associatedType, associatedUuid, s, e)
        .then((evts) => {
          refreshEvents(evts);
        })
        .catch((err) => {
          console.error('Error loading events:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }, [startStr, endStr, associatedType, associatedUuid, calendarEventStore, lastDates]);

    // Update events when store changes
    useEffect(() => {
      refreshEvents(calendarEventStore.eventList);
    }, [calendarEventStore.eventList.length, selectedEvent]);

    function refreshEvents(eventList: CalendarEvent[]) {
      let evts: RenderableEvent[] = [];
      if (eventList.length > 0) {
        evts = eventList
          .filter(
            (e) => e.associated_type === associatedType && e.associated_uuid === associatedUuid
          )
          .map((e) => getRenderableEvent(e));
      }
      if (selectedEvent && selectedEvent.id.startsWith(FAKE_PREFIX)) {
        evts = [...evts, selectedEvent];
      }
      setEvents(evts);
    }

    function handleDatesSet(info: EventSourceFuncArg) {
      if (info.startStr && info.endStr) {
        setStartStr(info.startStr);
        setEndStr(info.endStr);
      }
    }

    function changeView(view: string, date: string) {
      if (!timezoneStr || !calendarRef.current) return;

      const api = calendarRef.current.getApi();
      api.changeView(view);
      api.gotoDate(date);

      let scrollTime = moment(date);
      if (scrollTime.hour() > 0) {
        scrollTime = scrollTime.subtract(15, 'minutes');
      }
      api.scrollToTime(scrollTime.tz(timezoneStr).format('HH:mm:ss'));
    }

    function handleDateClick(info: DateClickArg) {
      const fakeId = FAKE_PREFIX + Math.random().toString(36).substring(7);
      const end = dayjs(info.dateStr).add(1, 'hour').format();

      const e: RenderableEvent = {
        id: fakeId,
        calendar_id: '',
        title: '',
        start: info.dateStr,
        end: end,
        allDay: false,
        editable: true,
        isUnsaved: true,
        backgroundColor: '#ff000022',
        borderColor: '#dd000044',
      };

      setSelectedEvent(e);
      changeView('timeGridDay', info.dateStr);
    }

    function handleEventClick(info: EventClickArg) {
      const event = calendarEventStore.eventList.find((e) => e.id === info.event.id);
      if (event) {
        setSelectedEvent(getRenderableEvent(event));
      }
    }

    function handleEventChange(change: EventChangeArg) {
      const event = change.event;
      if (event.id === selectedEvent?.id) {
        setSelectedEvent({
          ...selectedEvent,
          start: event.startStr,
          end: event.endStr,
        });
      }
    }

    // No calendar configured
    if (noCalendar) {
      return <NoCalendar />;
    }

    // Loading timezone
    if (!timezoneStr) {
      return (
        <Stack alignItems="center" justifyContent="center" p={4}>
          <CircularProgress />
          <Typography mt={2}>Loading...</Typography>
        </Stack>
      );
    }

    return (
      <Paper
        sx={{
          borderRadius: 2,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          overflow: 'auto',
        }}
      >
        {showChrome && backTo && (
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => navigate(backTo)}
          >
            <CloseIcon />
          </IconButton>
        )}

        <Stack spacing={2}>
          {loading && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={20} />
              <Typography color="text.secondary">Loading events...</Typography>
            </Stack>
          )}

          {showChrome && !calendarPreferences && (
            <Stack>
              <Typography variant="h6">Fancy Calendar</Typography>
              <Typography variant="body2" color="text.secondary">
                Let's plan together.
              </Typography>
            </Stack>
          )}

          {calendarPreferences && (
            <Stack>
              <Typography variant="subtitle2">Notes from the user</Typography>
              <Typography variant="body2">{calendarPreferences}</Typography>
            </Stack>
          )}

          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: selectedEvent?.id ? 6 : 12 }}>
              <Paper
                sx={{
                  height: {
                    xs: selectedEvent?.id ? '40vh' : '85vh',
                    sm: '85vh',
                  },
                  maxWidth: selectedEvent?.id ? 800 : 1100,
                  transition: 'max-width 0.2s',
                }}
              >
                <FullCalendar
                  ref={calendarRef}
                  height="100%"
                  plugins={[
                    dayGridPlugin,
                    timeGridPlugin,
                    listPlugin,
                    interactionPlugin,
                    rrulePlugin,
                    momentTimezonePlugin,
                  ]}
                  buttonText={{
                    today: 'Today',
                    month: 'Month',
                    week: 'Week',
                    day: 'Day',
                    list: 'List',
                  }}
                  selectMirror={true}
                  initialView="timeGridWeek"
                  slotDuration="00:30:00"
                  slotLabelInterval="01:00:00"
                  scrollTime="10:00:00"
                  nowIndicator={true}
                  timeZone={timezoneStr}
                  timeZoneParam={timezoneStr}
                  events={events}
                  businessHours={{
                    daysOfWeek: [1, 2, 3, 4, 5],
                    startTime: '08:00',
                    endTime: '18:00',
                  }}
                  headerToolbar={{
                    left: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                    center: 'title',
                    right: 'prev,next today',
                  }}
                  datesSet={handleDatesSet}
                  eventChange={handleEventChange}
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                />
                <Stack p={2} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    Timezone: {timezoneStr}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>

            {selectedEvent?.id && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <CalendarEventEditor
                  key={selectedEvent.id}
                  event={selectedEvent}
                  startStr={selectedEvent.start}
                  endStr={selectedEvent.end}
                  timezoneStr={timezoneStr}
                  associatedType={associatedType}
                  associatedUuid={associatedUuid}
                  calendarList={calendarList}
                  onCancel={() => setSelectedEvent(null)}
                  onDeleted={() => setSelectedEvent(null)}
                  onSaved={() => setSelectedEvent(null)}
                />
              </Grid>
            )}
          </Grid>
        </Stack>
      </Paper>
    );
  }
);

export default CalendarFrame;
